import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MessageCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { LEAD_FORM_ID } from '../utils/scroll';

/**
 * Persistent WhatsApp entry point.
 * - Mobile: full-width sticky bar at the bottom.
 * - Desktop: floating button in the bottom-right corner.
 *
 * Both appear only after the hero scrolls out and hide while the lead form is in
 * view, so they can never cover fields, validation messages, or the submit button.
 * Both open the shared QueenPicker — the visitor always chooses which Queen to message.
 */
export function WhatsAppCTA() {
  const {
    t
  } = useLanguage();
  const {
    openPicker
  } = useLead();
  const [pastHero, setPastHero] = useState(false);
  const [formInView, setFormInView] = useState(false);
  const reduce = useReducedMotion();
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const node = document.getElementById(LEAD_FORM_ID);
    if (!node) return;
    const observer = new IntersectionObserver(entries => entries.forEach(entry => setFormInView(entry.isIntersecting)), {
      threshold: 0
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const show = pastHero && !formInView;
  const motionProps = reduce ? {} : {
    initial: {
      opacity: 0,
      y: 15
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 15
    },
    transition: {
      duration: 0.3
    }
  };
  return <AnimatePresence data-id="element-273" data-mp="ve3g9o2a84he">
      {show && <>
          {/* Mobile sticky bar */}
          <motion.div key="whatsapp-bar" {...motionProps} className="fixed inset-x-0 bottom-0 z-40 border-t border-camel/40 bg-white/95 p-3 backdrop-blur-md md:hidden" data-id="element-274" data-mp="10dzipivbznuu">
            <button type="button" onClick={() => openPicker('sticky-mobile')} className="flex min-h-[52px] w-full items-center justify-center gap-2 bg-whatsapp px-5 text-sm font-bold uppercase tracking-[0.12em] text-golddeep" data-id="element-275" data-mp="ch38se6xhj29">
              <MessageCircleIcon aria-hidden="true" className="h-4 w-4" data-id="element-276" data-mp="1088likhknv8c" />
              {t('sticky.cta')}
            </button>
          </motion.div>

          {/* Desktop floating button */}
          <motion.div key="whatsapp-fab" {...motionProps} className="fixed bottom-6 right-6 z-40 hidden md:block" data-id="element-277" data-mp="q8zy6ah7peuy">
            <button type="button" onClick={() => openPicker('floating-desktop')} aria-label={t('sticky.cta')} className="flex min-h-[56px] items-center gap-3 rounded-full bg-whatsapp pl-4 pr-5 text-sm font-bold uppercase tracking-[0.12em] text-golddeep shadow-xl shadow-ink/20 transition-transform hover:-translate-y-[2px]" data-id="element-278" data-mp="lcixkgstt80z">
              <MessageCircleIcon aria-hidden="true" className="h-5 w-5" data-id="element-279" data-mp="1qfmwpfn6d4tk" />
              <span data-id="element-280" data-mp="1vg0wybijbah1">{t('sticky.cta')}</span>
            </button>
          </motion.div>
        </>}
    </AnimatePresence>;
}